import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, TextLink, Tooltip } from '@kontentino/ui';
import { faFlagSwallowtail } from '@fortawesome/pro-regular-svg-icons/faFlagSwallowtail';
import { formatDateTimeUTCToLocalTimezone } from 'app/utils/date';
import PostRevisionsModal from './PostRevisionsModal';
import DownloadPostAssetsDropdown from 'app/components/mediaAttachmentsDownload/DownloadPostAssetsDropdown';

type CurrentPostProps = {
  id: number;
  revision: number;
  createdAt: string;
};

type Props = {
  isCurrent?: boolean;
  revisionPostData: {
    hasAttachments: boolean;
  } & CurrentPostProps;
  currentPostData: CurrentPostProps;
};

const PostRevisionsListItem: FC<Props> = ({
  isCurrent,
  revisionPostData,
  currentPostData,
}) => {
  const { t } = useTranslation();
  const [
    isPostRevisionsComparisonModalOpen,
    setIsPostRevisionsComparisonModalOpen,
  ] = useState(false);

  function getFormatedRevisionTitle(revision: number, date: string) {
    const formattedRevision = String(revision).padStart(2, '0') + '.';
    const formattedDate = date ? formatDateTimeUTCToLocalTimezone(date) : null;

    return `${t('version')} ${formattedRevision}${
      formattedDate ? ` - ${formattedDate}` : ''
    }`;
  }

  return (
    <>
      <div className="tw-rounded-md tw-bg-grayscale-10 tw-px-2 tw-py-[6px] tw-text-sm tw-font-semibold">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon icon={faFlagSwallowtail} size={'sm'} />
          <p>
            {getFormatedRevisionTitle(
              revisionPostData.revision,
              revisionPostData.createdAt,
            )}
          </p>
        </div>
        {(revisionPostData.hasAttachments || !isCurrent) && (
          <div className="tw-mt-1 tw-flex tw-items-center tw-gap-4 tw-pl-6">
            {revisionPostData.hasAttachments && (
              <Tooltip
                className="tw-text-center tw-text-sm"
                content={
                  isCurrent
                    ? t('downloadAssetsFromTheLatestPostVersion')
                    : `${t(
                        'downloadAssetsFromTheOlderPostVersion',
                      )} ${getFormatedRevisionTitle(
                        revisionPostData.revision,
                        revisionPostData.createdAt,
                      )}`
                }
              >
                <div>
                  <DownloadPostAssetsDropdown
                    id={currentPostData.id}
                    revisionId={revisionPostData.id}
                  >
                    <Button variant="plain" size="small">
                      <TextLink>
                        <span>{t('downloadAssets')}</span>
                      </TextLink>
                    </Button>
                  </DownloadPostAssetsDropdown>
                </div>
              </Tooltip>
            )}
            {!isCurrent && (
              <TextLink>
                <Button
                  onClick={() => setIsPostRevisionsComparisonModalOpen(true)}
                  variant="plain"
                  size="small"
                >
                  {t('view')}
                </Button>
              </TextLink>
            )}
          </div>
        )}
      </div>

      {isPostRevisionsComparisonModalOpen && (
        <PostRevisionsModal
          isShown={isPostRevisionsComparisonModalOpen}
          onClose={() => setIsPostRevisionsComparisonModalOpen(false)}
          currentPost={{
            id: currentPostData.id,
            title: getFormatedRevisionTitle(
              currentPostData.revision,
              currentPostData.createdAt,
            ),
          }}
          revisionPost={{
            id: revisionPostData.id,
            title: getFormatedRevisionTitle(
              revisionPostData.revision,
              revisionPostData.createdAt,
            ),
          }}
        />
      )}
    </>
  );
};

export default PostRevisionsListItem;
